'use client'

import { SearchResults, useSearch } from '@/components/search/SearchPanel'
import { track } from '@/lib/tracking'
import { usePathname } from 'next/navigation'
import { useCallback, useEffect, useRef, useState } from 'react'
import { RiSearchLine } from 'react-icons/ri'

// The search control, to the identity mocks (2026-11-01):
//
//   as="bar"    the desktop header's box (header A). From 1280px it is a real
//               box: you type straight into it and the results drop down
//               under it. Narrower, an icon stands in and opens the panel.
//   as="icon"   an icon (the phone header) that opens the panel.
//   as="field"  the phone menu's box-shaped button; opens the panel.
//
// The panel is a box with the results under it: a dropdown at the header's
// right edge from 768px, a full-screen sheet with Cancel on a phone.
//
// `shortcuts`: Cmd/Ctrl+K anywhere, or "/" when the reader isn't typing,
// focuses the box (or opens the panel). Only one control on the page sets
// it (the desktop header), so a shortcut never opens two.

export default function SearchButton({
  location = 'header',
  as = 'icon',
  shortcuts = false,
}) {
  const [panel, setPanel] = useState(false)
  // Counts opens: the panel is keyed on it, so each open starts fresh.
  const [opens, setOpens] = useState(0)
  const boxRef = useRef(null)

  const openPanel = useCallback(
    (how) => {
      setPanel(true)
      setOpens((n) => n + 1)
      track('search_opened', { location, how })
    },
    [location]
  )

  useEffect(() => {
    if (!shortcuts) return undefined
    function onKey(e) {
      const typing = ['INPUT', 'TEXTAREA', 'SELECT'].includes(
        document.activeElement?.tagName
      )
      const cmdK = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k'
      const slash =
        e.key === '/' && !e.metaKey && !e.ctrlKey && !e.altKey && !typing
      if (!cmdK && !slash) return
      e.preventDefault()
      // The header box when it's on screen, else the panel.
      if (boxRef.current?.offsetParent) {
        boxRef.current.focus()
        track('search_opened', { location, how: 'shortcut' })
      } else openPanel('shortcut')
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [shortcuts, openPanel, location])

  const icon = (className = '') => (
    <button
      type="button"
      aria-label="Search"
      title="Search (⌘K)"
      onClick={() => openPanel('click')}
      className={`inline-flex h-10 w-10 items-center justify-center rounded-lg text-foreground transition-colors hover:bg-muted ${className}`}
    >
      <RiSearchLine className="h-5 w-5" aria-hidden="true" />
    </button>
  )

  return (
    <>
      {as === 'bar' ? (
        <>
          <InlineBox boxRef={boxRef} location={location} />
          {icon('xl:hidden')}
        </>
      ) : as === 'field' ? (
        <button
          type="button"
          onClick={() => openPanel('click')}
          className="flex min-h-12 w-full items-center gap-3 rounded-full border border-foreground bg-card px-4 text-left font-sans text-[15px] text-muted-foreground"
        >
          <RiSearchLine
            className="h-5 w-5 shrink-0 text-foreground"
            aria-hidden="true"
          />
          Search stories, topics and outlets
        </button>
      ) : (
        icon()
      )}
      {panel && (
        <FloatingPanel
          key={opens}
          location={location}
          onClose={() => setPanel(false)}
        />
      )}
    </>
  )
}

// The header's own box (from 1280px): type into it, results drop down under
// it. Escape, a click outside or opening a result closes them.
function InlineBox({ boxRef, location }) {
  const [focused, setFocused] = useState(false)
  const wrapRef = useRef(null)
  const pathname = usePathname()
  const s = useSearch({
    location,
    onDone: () => {
      setFocused(false)
      boxRef.current?.blur()
    },
  })

  // A new page closes the results.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setFocused(false)
  }, [pathname])

  useEffect(() => {
    if (!focused) return undefined
    function onDown(e) {
      if (!wrapRef.current?.contains(e.target)) setFocused(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [focused])

  const showing = focused && s.live
  return (
    <div ref={wrapRef} className="relative hidden xl:block">
      <label
        className={`flex h-10 items-center gap-2 rounded-lg border bg-card px-3 font-sans text-sm transition-[width,border-color] ${
          focused
            ? 'w-[400px] border-foreground'
            : 'w-[260px] border-border hover:border-foreground'
        }`}
      >
        <RiSearchLine
          className="h-[17px] w-[17px] shrink-0 text-foreground"
          aria-hidden="true"
        />
        <input
          ref={boxRef}
          type="search"
          value={s.query}
          onChange={(e) => s.setQuery(e.target.value)}
          onFocus={() => setFocused(true)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setFocused(false)
              e.currentTarget.blur()
              return
            }
            s.onKeyDown(e)
          }}
          placeholder="Search Pano"
          aria-label="Search Pano"
          role="combobox"
          aria-autocomplete="list"
          aria-controls="header-search-results"
          aria-expanded={showing}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="search"
          className="min-w-0 flex-1 bg-transparent text-foreground placeholder:text-muted-foreground focus:outline-none [&::-webkit-search-cancel-button]:hidden"
        />
        <kbd className="rounded border border-border bg-background px-1.5 py-px font-sans text-[11px] font-medium text-muted-foreground">
          {focused ? 'esc' : '⌘K'}
        </kbd>
      </label>
      {showing && (
        <div
          id="header-search-results"
          className="absolute right-0 top-[calc(100%+12px)] z-50 w-[560px] overflow-hidden rounded-lg border border-foreground bg-card shadow-xl"
        >
          <div className="max-h-[70vh] overflow-y-auto">
            <SearchResults s={s} />
          </div>
        </div>
      )}
    </div>
  )
}

// The panel with its own box: a dropdown under the header's right edge from
// 768px, a full-screen sheet with Cancel on a phone.
function FloatingPanel({ location, onClose }) {
  const inputRef = useRef(null)
  const s = useSearch({ location, onDone: onClose })

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 30)
    function onKey(e) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      clearTimeout(t)
      window.removeEventListener('keydown', onKey)
    }
  }, [onClose])

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-label="Search">
      {/* The page behind: dimmed on a desktop, a click closes. */}
      <button
        type="button"
        aria-label="Close search"
        onClick={onClose}
        className="absolute inset-0 hidden cursor-default bg-foreground/20 md:block"
      />
      <div className="absolute inset-0 flex flex-col bg-background md:inset-auto md:right-[max(16px,calc((100vw-1200px)/2))] md:top-[76px] md:max-h-[80vh] md:w-[560px] md:overflow-hidden md:rounded-lg md:border md:border-foreground md:bg-card md:shadow-xl">
        <div className="flex items-center gap-2 border-b border-foreground px-3 py-2.5 md:border-border">
          <label className="flex h-11 min-w-0 flex-1 items-center gap-2 rounded-lg border border-foreground bg-card px-3 md:border-0 md:bg-transparent md:px-1">
            <RiSearchLine
              className="h-[18px] w-[18px] shrink-0 text-muted-foreground"
              aria-hidden="true"
            />
            <input
              ref={inputRef}
              type="search"
              value={s.query}
              onChange={(e) => s.setQuery(e.target.value)}
              onKeyDown={s.onKeyDown}
              placeholder="Search Pano"
              aria-label="Search Pano"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              enterKeyHint="search"
              className="min-w-0 flex-1 bg-transparent font-sans text-base text-foreground placeholder:text-muted-foreground focus:outline-none [&::-webkit-search-cancel-button]:hidden"
            />
          </label>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 px-1.5 font-sans text-[15px] text-foreground md:hidden"
          >
            Cancel
          </button>
          <kbd className="hidden rounded border border-border px-1.5 py-px font-sans text-[11px] font-medium text-muted-foreground md:inline">
            esc
          </kbd>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <SearchResults s={s} />
        </div>
      </div>
    </div>
  )
}
